import React, { useState, useEffect } from 'react';
import { Permission } from '@/api/entities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, 
  Plus, 
  Edit, 
  Trash2, 
  Key,
  Calendar,
  FileText
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function RightsRegistry() {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const permissionsData = await Permission.list();
      setPermissions(permissionsData.permissions || []);
    } catch (err) {
      console.error('Error loading rights registry data:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePermission = async () => {
    try {
      await Permission.create(formData);
      setIsCreateDialogOpen(false);
      setFormData({ name: '', description: '' });
      await loadData();
    } catch (err) {
      console.error('Error creating permission:', err);
      setError('Ошибка создания права');
    }
  };

  const handleEditPermission = async () => {
    try {
      await Permission.update(selectedPermission.id, formData);
      setIsEditDialogOpen(false);
      setSelectedPermission(null);
      setFormData({ name: '', description: '' });
      await loadData();
    } catch (err) {
      console.error('Error updating permission:', err);
      setError('Ошибка обновления права');
    }
  };

  const handleDeletePermission = async (permissionId) => {
    if (!confirm('Вы уверены, что хотите удалить это право?')) {
      return;
    }
    try {
      await Permission.delete(permissionId);
      await loadData();
    } catch (err) {
      console.error('Error deleting permission:', err);
      setError('Ошибка удаления права');
    }
  };

  const openEditDialog = (permission) => {
    setSelectedPermission(permission);
    setFormData({
      name: permission.name,
      description: permission.description || ''
    });
    setIsEditDialogOpen(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPermissionType = (name) => {
    if (name.includes('.')) {
      const [resource, action] = name.split('.');
      return { resource, action };
    }
    return { resource: 'system', action: name };
  };

  if (isLoading) {
    return (
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            Реестр прав
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-slate-600">Загрузка...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="elegant-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            Реестр прав
          </CardTitle>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700">
                <Plus className="w-4 h-4 mr-2" />
                Добавить право
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Создать право</DialogTitle>
                <DialogDescription>
                  Заполните данные для создания нового права доступа
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Название права</Label>
                  <Input
                    id="name"
                    placeholder="resource.action (например: users.read)"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Формат: ресурс.действие (например: users.read, orders.create)
                  </p>
                </div>
                <div>
                  <Label htmlFor="description">Описание</Label>
                  <Textarea
                    id="description"
                    placeholder="Описание назначения права"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Отмена
                </Button>
                <Button onClick={handleCreatePermission}>
                  Создать
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Право</TableHead>
                <TableHead>Ресурс</TableHead>
                <TableHead>Действие</TableHead>
                <TableHead>Описание</TableHead>
                <TableHead>Дата создания</TableHead>
                <TableHead className="text-right">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map((permission) => {
                const { resource, action } = getPermissionType(permission.name);
                return (
                  <TableRow key={permission.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Key className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium font-mono">{permission.name}</div>
                          <div className="text-sm text-slate-500">ID: {permission.id.slice(0, 8)}...</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-slate-50">
                        {resource}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {action}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="max-w-xs">
                        {permission.description ? (
                          <div className="flex items-start gap-1">
                            <FileText className="w-3 h-3 mt-0.5 text-slate-400" />
                            <span className="text-sm text-slate-600">{permission.description}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-slate-400">Нет описания</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(permission.created_date)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(permission)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeletePermission(permission.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактировать право</DialogTitle>
            <DialogDescription>
              Измените данные права доступа
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Название права</Label>
              <Input
                id="edit_name"
                placeholder="resource.action (например: users.read)"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Формат: ресурс.действие (например: users.read, orders.create)
              </p>
            </div>
            <div>
              <Label htmlFor="edit_description">Описание</Label>
              <Textarea
                id="edit_description"
                placeholder="Описание назначения права"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Отмена
            </Button>
            <Button onClick={handleEditPermission}>
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
